'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
interface AvailabilitySlot {
  id: string;
  day: string;
  dayKey: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
}

interface WorkingHoursRow {
  day: string;
  dayKey: string;
  enabled: boolean;
  startTime: string;
  endTime: string;
}

interface BlockedDate {
  id: string;
  date: string;
  reason: string;
}

interface ConsultantSettings {
  instantBookingEnabled: boolean;
  slotDurationMinutes: number;
  bookingBufferMinutes: number;
  advanceBookingDays: number;
  timezone: string;
  availabilityStatus: string;
}

const DAYS_OF_WEEK: { key: string; label: string }[] = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const SLOT_DURATIONS = [30, 45, 60, 90, 120];
const BUFFER_OPTIONS = [0, 10, 15, 30];
const ADVANCE_DAYS_OPTIONS = [7, 14, 30, 60, 90];
const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Dubai', 'Asia/Karachi',
  'Asia/Dhaka', 'Asia/Kolkata', 'Asia/Jakarta', 'Asia/Kuala_Lumpur', 'Asia/Singapore',
  'Africa/Nairobi', 'Africa/Lagos', 'Australia/Sydney',
];

const DEFAULT_WORKING_HOURS: WorkingHoursRow[] = DAYS_OF_WEEK.map(d => ({
  day: d.label,
  dayKey: d.key,
  enabled: !['saturday', 'sunday'].includes(d.key),
  startTime: '09:00',
  endTime: '17:00',
}));

function generateTimeOptions(): string[] {
  const times: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      times.push(`${hh}:${mm}`);
    }
  }
  return times;
}

const TIME_OPTIONS = generateTimeOptions();

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function ConsultantAvailabilityPage() {
  const supabase = createClient();

  const [profileId, setProfileId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'working-hours' | 'slots' | 'blocked' | 'settings'>('working-hours');

  // Working hours
  const [workingHours, setWorkingHours] = useState<WorkingHoursRow[]>(DEFAULT_WORKING_HOURS);

  // Specific slots
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [newSlot, setNewSlot] = useState({ day: 'monday', startTime: '09:00', endTime: '10:00' });
  const [slotSaving, setSlotSaving] = useState(false);

  // Blocked dates
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [newBlockedDate, setNewBlockedDate] = useState('');
  const [newBlockedReason, setNewBlockedReason] = useState('');
  const [blockSaving, setBlockSaving] = useState(false);

  // Settings
  const [settings, setSettings] = useState<ConsultantSettings>({
    instantBookingEnabled: false,
    slotDurationMinutes: 60,
    bookingBufferMinutes: 15,
    advanceBookingDays: 30,
    timezone: 'UTC',
    availabilityStatus: 'available',
  });

  // ─── Load Data ──────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Please sign in to manage your availability.');
        setLoading(false);
        return;
      }

      // Fetch consultant profile
      const { data: profile, error: profileError } = await supabase
        .from('consultant_profiles')
        .select('id, instant_booking_enabled, slot_duration_minutes, booking_buffer_minutes, advance_booking_days, timezone, availability_status')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) {
        setError(profileError.message);
        setLoading(false);
        return;
      }

      if (!profile) {
        setError('No consultant profile found. Please complete your consultant onboarding first.');
        setLoading(false);
        return;
      }

      setProfileId(profile.id);
      setSettings({
        instantBookingEnabled: profile.instant_booking_enabled ?? false,
        slotDurationMinutes: profile.slot_duration_minutes ?? 60,
        bookingBufferMinutes: profile.booking_buffer_minutes ?? 15,
        advanceBookingDays: profile.advance_booking_days ?? 30,
        timezone: profile.timezone ?? 'UTC',
        availabilityStatus: profile.availability_status ?? 'available',
      });

      // Fetch availability slots
      const { data: slotData } = await supabase
        .from('consultant_availability_slots')
        .select('id, day_of_week, start_time, end_time, is_active')
        .eq('consultant_profile_id', profile.id)
        .order('day_of_week');

      const mappedSlots: AvailabilitySlot[] = (slotData ?? []).map(s => ({
        id: s.id,
        day: s.day_of_week.charAt(0).toUpperCase() + s.day_of_week.slice(1),
        dayKey: s.day_of_week,
        startTime: (s.start_time ?? '').slice(0, 5),
        endTime: (s.end_time ?? '').slice(0, 5),
        isActive: s.is_active ?? true,
      }));
      setSlots(mappedSlots);

      // Build working hours from slots (aggregate per day)
      if (mappedSlots.length > 0) {
        const updatedHours = DEFAULT_WORKING_HOURS.map(row => {
          const daySlots = mappedSlots.filter(s => s.dayKey === row.dayKey && s.isActive);
          if (daySlots.length > 0) {
            const earliest = daySlots.reduce((a, b) => a.startTime < b.startTime ? a : b);
            const latest = daySlots.reduce((a, b) => a.endTime > b.endTime ? a : b);
            return { ...row, enabled: true, startTime: earliest.startTime, endTime: latest.endTime };
          }
          return row;
        });
        setWorkingHours(updatedHours);
      }

      // Fetch blocked dates
      const { data: blockedData } = await supabase
        .from('consultant_blocked_dates')
        .select('id, blocked_date, reason')
        .eq('consultant_profile_id', profile.id)
        .order('blocked_date');

      setBlockedDates((blockedData ?? []).map(b => ({
        id: b.id,
        date: b.blocked_date,
        reason: b.reason ?? '',
      })));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load availability data');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Save Working Hours ─────────────────────────────────────────────────────
  const saveWorkingHours = async () => {
    if (!profileId) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      // Delete existing slots for enabled days, then re-insert
      const enabledDays = workingHours.filter(r => r.enabled).map(r => r.dayKey);
      const disabledDays = workingHours.filter(r => !r.enabled).map(r => r.dayKey);

      // Remove slots for disabled days
      if (disabledDays.length > 0) {
        await supabase
          .from('consultant_availability_slots')
          .delete()
          .eq('consultant_profile_id', profileId)
          .in('day_of_week', disabledDays);
      }

      // Upsert slots for enabled days
      for (const row of workingHours.filter(r => r.enabled)) {
        // Check if slot exists for this day
        const { data: existing } = await supabase
          .from('consultant_availability_slots')
          .select('id')
          .eq('consultant_profile_id', profileId)
          .eq('day_of_week', row.dayKey)
          .maybeSingle();

        if (existing) {
          await supabase
            .from('consultant_availability_slots')
            .update({ start_time: row.startTime, end_time: row.endTime, is_active: true })
            .eq('id', existing.id);
        } else {
          await supabase
            .from('consultant_availability_slots')
            .insert({
              consultant_profile_id: profileId,
              day_of_week: row.dayKey,
              start_time: row.startTime,
              end_time: row.endTime,
              is_active: true,
            });
        }
      }

      // Reload slots
      const { data: slotData } = await supabase
        .from('consultant_availability_slots')
        .select('id, day_of_week, start_time, end_time, is_active')
        .eq('consultant_profile_id', profileId)
        .order('day_of_week');

      setSlots((slotData ?? []).map(s => ({
        id: s.id,
        day: s.day_of_week.charAt(0).toUpperCase() + s.day_of_week.slice(1),
        dayKey: s.day_of_week,
        startTime: (s.start_time ?? '').slice(0, 5),
        endTime: (s.end_time ?? '').slice(0, 5),
        isActive: s.is_active ?? true,
      })));

      setSaveMsg('Working hours saved successfully!');
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (err: unknown) {
      setSaveMsg(err instanceof Error ? err.message : 'Failed to save working hours');
    } finally {
      setSaving(false);
    }
  };

  // ─── Add Specific Slot ──────────────────────────────────────────────────────
  const addSlot = async () => {
    if (!profileId) return;
    setSlotSaving(true);
    try {
      const { data, error: insertError } = await supabase
        .from('consultant_availability_slots')
        .insert({
          consultant_profile_id: profileId,
          day_of_week: newSlot.day,
          start_time: newSlot.startTime,
          end_time: newSlot.endTime,
          is_active: true,
        })
        .select('id, day_of_week, start_time, end_time, is_active')
        .maybeSingle();

      if (insertError) throw insertError;
      if (data) {
        setSlots(prev => [...prev, {
          id: data.id,
          day: data.day_of_week.charAt(0).toUpperCase() + data.day_of_week.slice(1),
          dayKey: data.day_of_week,
          startTime: (data.start_time ?? '').slice(0, 5),
          endTime: (data.end_time ?? '').slice(0, 5),
          isActive: data.is_active ?? true,
        }]);
      }
      setShowAddSlot(false);
      setNewSlot({ day: 'monday', startTime: '09:00', endTime: '10:00' });
    } catch (err: unknown) {
      setSaveMsg(err instanceof Error ? err.message : 'Failed to add slot');
    } finally {
      setSlotSaving(false);
    }
  };

  // ─── Toggle Slot ────────────────────────────────────────────────────────────
  const toggleSlot = async (id: string) => {
    const slot = slots.find(s => s.id === id);
    if (!slot) return;
    const newActive = !slot.isActive;
    await supabase.from('consultant_availability_slots').update({ is_active: newActive }).eq('id', id);
    setSlots(prev => prev.map(s => s.id === id ? { ...s, isActive: newActive } : s));
  };

  // ─── Delete Slot ────────────────────────────────────────────────────────────
  const deleteSlot = async (id: string) => {
    await supabase.from('consultant_availability_slots').delete().eq('id', id);
    setSlots(prev => prev.filter(s => s.id !== id));
  };

  // ─── Add Blocked Date ───────────────────────────────────────────────────────
  const addBlockedDate = async () => {
    if (!profileId || !newBlockedDate) return;
    setBlockSaving(true);
    try {
      const { data, error: insertError } = await supabase
        .from('consultant_blocked_dates')
        .insert({ consultant_profile_id: profileId, blocked_date: newBlockedDate, reason: newBlockedReason })
        .select('id, blocked_date, reason')
        .maybeSingle();
      if (insertError) throw insertError;
      if (data) {
        setBlockedDates(prev => [...prev, { id: data.id, date: data.blocked_date, reason: data.reason ?? '' }]);
      }
      setNewBlockedDate('');
      setNewBlockedReason('');
    } catch (err: unknown) {
      setSaveMsg(err instanceof Error ? err.message : 'Failed to block date');
    } finally {
      setBlockSaving(false);
    }
  };

  // ─── Remove Blocked Date ────────────────────────────────────────────────────
  const removeBlockedDate = async (id: string) => {
    await supabase.from('consultant_blocked_dates').delete().eq('id', id);
    setBlockedDates(prev => prev.filter(b => b.id !== id));
  };

  // ─── Save Settings ──────────────────────────────────────────────────────────
  const saveSettings = async () => {
    if (!profileId) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const { error: updateError } = await supabase
        .from('consultant_profiles')
        .update({
          instant_booking_enabled: settings.instantBookingEnabled,
          slot_duration_minutes: settings.slotDurationMinutes,
          booking_buffer_minutes: settings.bookingBufferMinutes,
          advance_booking_days: settings.advanceBookingDays,
          timezone: settings.timezone,
          availability_status: settings.availabilityStatus,
        })
        .eq('id', profileId);
      if (updateError) throw updateError;
      setSaveMsg('Settings saved successfully!');
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (err: unknown) {
      setSaveMsg(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <AppLayout activeRoute="/consultant-availability">
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            <p className="text-sm text-muted-foreground">Loading availability settings…</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout activeRoute="/consultant-availability">
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-sm">
            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
              <Icon name="ExclamationCircleIcon" size={28} className="text-red-500" />
            </div>
            <h3 className="text-base font-600 text-foreground mb-2">{error}</h3>
            <a href="/consultant-onboarding" className="text-sm text-primary font-500 hover:underline">
              Complete Consultant Onboarding →
            </a>
          </div>
        </div>
      </AppLayout>
    );
  }

  const activeSlotCount = slots.filter(s => s.isActive).length;

  return (
    <AppLayout activeRoute="/consultant-availability">
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b border-border bg-card px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="CalendarDaysIcon" size={20} className="text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-700 text-foreground">Availability Manager</h1>
                <p className="text-sm text-muted-foreground">Set your working hours and manage bookable slots</p>
              </div>
            </div>
            {/* Status badges */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className={`flex items-center gap-1.5 text-xs font-600 px-3 py-1.5 rounded-full ${
                settings.instantBookingEnabled
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :'bg-muted text-muted-foreground border border-border'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${settings.instantBookingEnabled ? 'bg-emerald-500' : 'bg-muted-foreground'}`} />
                {settings.instantBookingEnabled ? 'Instant Booking ON' : 'Instant Booking OFF'}
              </span>
              <span className="text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-full border border-border">
                {activeSlotCount} active slot{activeSlotCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Save message */}
          {saveMsg && (
            <div className={`mb-4 px-4 py-3 rounded-xl text-sm font-500 flex items-center gap-2 ${
              saveMsg.includes('success') || saveMsg.includes('saved')
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :'bg-red-50 text-red-700 border border-red-200'
            }`}>
              <Icon name={saveMsg.includes('success') || saveMsg.includes('saved') ? 'CheckCircleIcon' : 'ExclamationCircleIcon'} size={16} />
              {saveMsg}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 bg-muted p-1 rounded-xl mb-6 overflow-x-auto">
            {[
              { key: 'working-hours', label: 'Working Hours', icon: 'ClockIcon' },
              { key: 'slots', label: 'Time Slots', icon: 'CalendarIcon' },
              { key: 'blocked', label: 'Blocked Dates', icon: 'NoSymbolIcon' },
              { key: 'settings', label: 'Booking Settings', icon: 'Cog6ToothIcon' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-600 transition-all duration-150 whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.key
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={15} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Tab: Working Hours ── */}
          {activeTab === 'working-hours' && (
            <div className="space-y-4">
              <div className="bg-primary/5 border border-primary/20 rounded-2xl p-4 flex items-start gap-3">
                <Icon name="InformationCircleIcon" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">
                  Set your regular weekly working hours. These define when clients can book sessions with you. 
                  Enable <strong>Instant Booking</strong> in Settings to let clients book directly without approval.
                </p>
              </div>

              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-border">
                  <h2 className="text-sm font-700 text-foreground flex items-center gap-2">
                    <Icon name="ClockIcon" size={16} className="text-primary" />
                    Weekly Schedule
                  </h2>
                </div>
                <div className="divide-y divide-border">
                  {workingHours.map((row, idx) => (
                    <div key={row.dayKey} className={`flex items-center gap-4 px-5 py-4 transition-colors ${row.enabled ? '' : 'opacity-50'}`}>
                      {/* Toggle */}
                      <button
                        onClick={() => {
                          const updated = [...workingHours];
                          updated[idx] = { ...updated[idx], enabled: !updated[idx].enabled };
                          setWorkingHours(updated);
                        }}
                        className={`relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${
                          row.enabled ? 'bg-primary' : 'bg-muted-foreground/30'
                        }`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                          row.enabled ? 'translate-x-5' : 'translate-x-0.5'
                        }`} />
                      </button>

                      {/* Day label */}
                      <span className="w-24 text-sm font-600 text-foreground flex-shrink-0">{row.day}</span>

                      {/* Time range */}
                      {row.enabled ? (
                        <div className="flex items-center gap-2 flex-1">
                          <select
                            value={row.startTime}
                            onChange={e => {
                              const updated = [...workingHours];
                              updated[idx] = { ...updated[idx], startTime: e.target.value };
                              setWorkingHours(updated);
                            }}
                            className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                          >
                            {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <span className="text-muted-foreground text-sm">to</span>
                          <select
                            value={row.endTime}
                            onChange={e => {
                              const updated = [...workingHours];
                              updated[idx] = { ...updated[idx], endTime: e.target.value };
                              setWorkingHours(updated);
                            }}
                            className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                          >
                            {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <span className="text-xs text-muted-foreground ml-1">
                            {(() => {
                              const [sh, sm] = row.startTime.split(':').map(Number);
                              const [eh, em] = row.endTime.split(':').map(Number);
                              const diff = (eh * 60 + em) - (sh * 60 + sm);
                              return diff > 0 ? `${Math.floor(diff / 60)}h ${diff % 60 > 0 ? `${diff % 60}m` : ''}` : '';
                            })()}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Unavailable</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={saveWorkingHours}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-xl text-sm font-700 hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {saving ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name="CheckIcon" size={16} />
                  )}
                  Save Working Hours
                </button>
              </div>
            </div>
          )}

          {/* ── Tab: Time Slots ── */}
          {activeTab === 'slots' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-700 text-foreground">Specific Time Slots</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Add or manage individual bookable slots per day</p>
                </div>
                <button
                  onClick={() => setShowAddSlot(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-600 hover:bg-primary/90 transition-colors"
                >
                  <Icon name="PlusIcon" size={16} />
                  Add Slot
                </button>
              </div>

              {/* Add Slot Form */}
              {showAddSlot && (
                <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5">
                  <h3 className="text-sm font-700 text-foreground mb-4">New Time Slot</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1.5 block">Day</label>
                      <select
                        value={newSlot.day}
                        onChange={e => setNewSlot(p => ({ ...p, day: e.target.value }))}
                        className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      >
                        {DAYS_OF_WEEK.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1.5 block">Start Time</label>
                      <select
                        value={newSlot.startTime}
                        onChange={e => setNewSlot(p => ({ ...p, startTime: e.target.value }))}
                        className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      >
                        {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1.5 block">End Time</label>
                      <select
                        value={newSlot.endTime}
                        onChange={e => setNewSlot(p => ({ ...p, endTime: e.target.value }))}
                        className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      >
                        {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={addSlot}
                      disabled={slotSaving}
                      className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-xl text-sm font-600 hover:bg-primary/90 transition-colors disabled:opacity-60"
                    >
                      {slotSaving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="PlusIcon" size={15} />}
                      Add Slot
                    </button>
                    <button
                      onClick={() => setShowAddSlot(false)}
                      className="px-5 py-2 rounded-xl text-sm font-600 text-muted-foreground hover:bg-muted transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Slots grouped by day */}
              {DAYS_OF_WEEK.map(d => {
                const daySlots = slots.filter(s => s.dayKey === d.key);
                if (daySlots.length === 0) return null;
                return (
                  <div key={d.key} className="bg-card border border-border rounded-2xl overflow-hidden">
                    <div className="px-5 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
                      <span className="text-sm font-700 text-foreground">{d.label}</span>
                      <span className="text-xs text-muted-foreground">{daySlots.filter(s => s.isActive).length} active</span>
                    </div>
                    <div className="divide-y divide-border">
                      {daySlots.map(slot => (
                        <div key={slot.id} className="flex items-center gap-4 px-5 py-3">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${slot.isActive ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
                          <span className="text-sm font-600 text-foreground flex-1">
                            {slot.startTime} – {slot.endTime}
                          </span>
                          <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${
                            slot.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground'
                          }`}>
                            {slot.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <button
                            onClick={() => toggleSlot(slot.id)}
                            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                            title={slot.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <Icon name={slot.isActive ? 'PauseIcon' : 'PlayIcon'} size={15} />
                          </button>
                          <button
                            onClick={() => deleteSlot(slot.id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
                            title="Delete slot"
                          >
                            <Icon name="TrashIcon" size={15} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {slots.length === 0 && !showAddSlot && (
                <div className="text-center py-12 bg-card border border-border rounded-2xl">
                  <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-3">
                    <Icon name="CalendarIcon" size={24} className="text-muted-foreground" />
                  </div>
                  <p className="text-sm font-600 text-foreground mb-1">No time slots yet</p>
                  <p className="text-xs text-muted-foreground mb-4">Save your working hours first, or add specific slots manually.</p>
                  <button
                    onClick={() => setShowAddSlot(true)}
                    className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-600 hover:bg-primary/90 transition-colors"
                  >
                    Add First Slot
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Blocked Dates ── */}
          {activeTab === 'blocked' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
                <Icon name="NoSymbolIcon" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Block specific dates when you're unavailable (holidays, travel, personal days). 
                  Clients won't be able to book sessions on these dates.
                </p>
              </div>

              {/* Add blocked date */}
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="text-sm font-700 text-foreground mb-4">Block a Date</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1.5 block">Date</label>
                    <input
                      type="date"
                      value={newBlockedDate}
                      onChange={e => setNewBlockedDate(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1.5 block">Reason (optional)</label>
                    <input
                      type="text"
                      value={newBlockedReason}
                      onChange={e => setNewBlockedReason(e.target.value)}
                      placeholder="e.g. Public holiday, Travel, Personal day"
                      className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                </div>
                <button
                  onClick={addBlockedDate}
                  disabled={!newBlockedDate || blockSaving}
                  className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-xl text-sm font-600 hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {blockSaving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="NoSymbolIcon" size={15} />}
                  Block Date
                </button>
              </div>

              {/* Blocked dates list */}
              {blockedDates.length > 0 ? (
                <div className="bg-card border border-border rounded-2xl overflow-hidden">
                  <div className="px-5 py-3 border-b border-border">
                    <span className="text-sm font-700 text-foreground">{blockedDates.length} Blocked Date{blockedDates.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="divide-y divide-border">
                    {blockedDates.map(bd => (
                      <div key={bd.id} className="flex items-center gap-4 px-5 py-3">
                        <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                          <Icon name="NoSymbolIcon" size={15} className="text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-600 text-foreground">{bd.date}</p>
                          {bd.reason && <p className="text-xs text-muted-foreground truncate">{bd.reason}</p>}
                        </div>
                        <button
                          onClick={() => removeBlockedDate(bd.id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
                          title="Remove block"
                        >
                          <Icon name="XMarkIcon" size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-10 bg-card border border-border rounded-2xl">
                  <Icon name="CalendarDaysIcon" size={28} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No blocked dates. You're available every working day.</p>
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Booking Settings ── */}
          {activeTab === 'settings' && (
            <div className="space-y-5">
              {/* Instant Booking Toggle */}
              <div className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <Icon name="BoltIcon" size={20} className="text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-700 text-foreground">Instant Booking</h3>
                      <p className="text-xs text-muted-foreground mt-0.5 max-w-sm">
                        Allow clients to book sessions instantly without your approval. 
                        Live available slots will be shown on your profile.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSettings(p => ({ ...p, instantBookingEnabled: !p.instantBookingEnabled }))}
                    className={`relative w-12 h-6 rounded-full transition-colors duration-200 flex-shrink-0 mt-1 ${
                      settings.instantBookingEnabled ? 'bg-emerald-500' : 'bg-muted-foreground/30'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                      settings.instantBookingEnabled ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                {settings.instantBookingEnabled && (
                  <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-emerald-600 flex-shrink-0" />
                    <p className="text-xs text-emerald-700 font-500">
                      Instant booking is active. Clients can book your available slots directly.
                    </p>
                  </div>
                )}
              </div>

              {/* Availability Status */}
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="text-sm font-700 text-foreground mb-3 flex items-center gap-2">
                  <Icon name="SignalIcon" size={16} className="text-primary" />
                  Availability Status
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: 'available', label: 'Available', color: 'emerald', desc: 'Accepting bookings' },
                    { key: 'limited', label: 'Limited', color: 'amber', desc: 'Few slots left' },
                    { key: 'busy', label: 'Busy', color: 'red', desc: 'Not accepting' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => setSettings(p => ({ ...p, availabilityStatus: opt.key }))}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        settings.availabilityStatus === opt.key
                          ? `border-${opt.color}-400 bg-${opt.color}-50`
                          : 'border-border hover:border-primary/30'
                      }`}
                    >
                      <div className={`w-2 h-2 rounded-full mb-2 ${
                        opt.color === 'emerald' ? 'bg-emerald-500' : opt.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                      }`} />
                      <p className="text-sm font-600 text-foreground">{opt.label}</p>
                      <p className="text-xs text-muted-foreground">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Slot Duration & Buffer */}
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="text-sm font-700 text-foreground mb-4 flex items-center gap-2">
                  <Icon name="ClockIcon" size={16} className="text-primary" />
                  Session Defaults
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2 block">Default Slot Duration</label>
                    <div className="flex gap-2 flex-wrap">
                      {SLOT_DURATIONS.map(d => (
                        <button
                          key={d}
                          onClick={() => setSettings(p => ({ ...p, slotDurationMinutes: d }))}
                          className={`px-3 py-1.5 rounded-lg text-sm font-600 border-2 transition-all ${
                            settings.slotDurationMinutes === d
                              ? 'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/40'
                          }`}
                        >
                          {d < 60 ? `${d}m` : `${d / 60}h`}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2 block">Buffer Between Sessions</label>
                    <div className="flex gap-2 flex-wrap">
                      {BUFFER_OPTIONS.map(b => (
                        <button
                          key={b}
                          onClick={() => setSettings(p => ({ ...p, bookingBufferMinutes: b }))}
                          className={`px-3 py-1.5 rounded-lg text-sm font-600 border-2 transition-all ${
                            settings.bookingBufferMinutes === b
                              ? 'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/40'
                          }`}
                        >
                          {b === 0 ? 'None' : `${b}m`}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Advance Booking & Timezone */}
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="text-sm font-700 text-foreground mb-4 flex items-center gap-2">
                  <Icon name="GlobeAltIcon" size={16} className="text-primary" />
                  Booking Window & Timezone
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2 block">Advance Booking Window</label>
                    <div className="flex gap-2 flex-wrap">
                      {ADVANCE_DAYS_OPTIONS.map(d => (
                        <button
                          key={d}
                          onClick={() => setSettings(p => ({ ...p, advanceBookingDays: d }))}
                          className={`px-3 py-1.5 rounded-lg text-sm font-600 border-2 transition-all ${
                            settings.advanceBookingDays === d
                              ? 'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/40'
                          }`}
                        >
                          {d}d
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">How far in advance clients can book</p>
                  </div>
                  <div>
                    <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2 block">Your Timezone</label>
                    <select
                      value={settings.timezone}
                      onChange={e => setSettings(p => ({ ...p, timezone: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={saveSettings}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-xl text-sm font-700 hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {saving ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name="CheckIcon" size={16} />
                  )}
                  Save Settings
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
